"use client";
import { useState, useMemo, useCallback, useEffect } from "react";
import { groupBy, keys, sortBy } from "lodash";
import { useSearchParams, useRouter } from "next/navigation";
import {
  TextInput,
  Select,
  UnstyledButton,
  Anchor as MantineAnchor,
  Box as MantineBox,
  Text as MantineText,
  Title as MantineTitle,
} from "@mantine/core";
import { findVendorBySlug } from "@/lib/shared/utils";
import { getVendorType } from "@/lib/shared/constants";
import { useMobileHistoryState } from "@/hooks/useMobileHistoryState";
import EntitySlideIn, { EntitySlideInData } from "@/components/EntitySlideIn";
import ChevronRightIcon from "@/icons/ChevronRight";

import styles from "./styles.module.scss";

const mapVendorToEntity = (vendor?: Vendor): EntitySlideInData | undefined => {
  if (!vendor) return undefined;
  return {
    id: vendor.id,
    label: vendor.label,
    description: vendor.description,
    imageUrl: vendor.vendor_logo,
    website: vendor.link,
    instagram: vendor.instagram,
    subtitle: vendor.type !== undefined ? getVendorType(vendor.type) : undefined,
    doesRenting: vendor.does_renting,
    region: vendor.region,
  };
};

const VendorsList = ({ vendors }: { vendors: VendorsResponse }) => {
  const searchParams = useSearchParams();
  const router = useRouter();
  const [activeVendor, setActiveVendor] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [orderBy, setOrderBy] = useState<string | null>("country");

  useEffect(() => {
    const vendorParam = searchParams.get("vendor");
    if (vendorParam) {
      const vendor = findVendorBySlug(vendors, vendorParam);
      if (vendor) {
        setActiveVendor(vendor.label);
      }
    }
  }, [searchParams, vendors]);

  const filteredVendors = useMemo(() => {
    return vendors.filter((vendor: Vendor) => {
      if (vendor.included === 0) return false;

      if (searchQuery && !vendor.label.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }

      if (selectedCountry && vendor.region !== selectedCountry) {
        return false;
      }

      return true;
    });
  }, [vendors, searchQuery, selectedCountry]);

  const groupedVendors = useMemo(() => {
    if (orderBy === "alphabet") {
      const grouped = groupBy(filteredVendors, (vendor: Vendor) => {
        const firstChar = vendor.label.charAt(0).toUpperCase();
        return /[A-Z]/.test(firstChar) ? firstChar : "Special";
      });
      return grouped as Record<string, Vendor[]>;
    } else if (orderBy === "type") {
      const grouped = groupBy(filteredVendors, (vendor: Vendor) => {
        return getVendorType(vendor.type);
      });
      return grouped as Record<string, Vendor[]>;
    } else {
      const grouped = groupBy(filteredVendors, "region");
      return grouped as Record<string, Vendor[]>;
    }
  }, [filteredVendors, orderBy]);

  const sortedGroups = useMemo(() => {
    const groups = keys(groupedVendors);

    if (orderBy === "alphabet") {
      return sortBy(groups, (group) => {
        if (group === "Special") return "!";
        return group;
      });
    } else {
      return sortBy(groups);
    }
  }, [groupedVendors, orderBy]);

  const countries = useMemo(() => {
    const uniqueCountries = Array.from(new Set(vendors.map((v: Vendor) => v.region)));
    return sortBy(uniqueCountries)
      .filter((country): country is string => country !== null)
      .map((country) => ({
        value: country,
        label: country,
      }));
  }, [vendors]);

  const onClick = useCallback(
    (vendor: Vendor) => {
      if (activeVendor === vendor.label) {
        setActiveVendor(null);
        router.push("/vendors", { scroll: false });
      } else {
        setActiveVendor(vendor.label);
      }
    },
    [activeVendor, router]
  );

  const closeSlideIn = () => {
    setActiveVendor(null);
    router.push("/vendors", { scroll: false });
  };

  const activeVendorData = useMemo(() => {
    return vendors.find((vendor: Vendor) => vendor.label === activeVendor);
  }, [activeVendor, vendors]);

  useMobileHistoryState({
    isOpen: !!activeVendor,
    onClose: closeSlideIn,
    modalId: "vendor-slidein",
  });

  return (
    <>
      <MantineBox className={styles.introText}>
        <MantineText>
          If there are vendors you would like us to add please reach out at{" "}
          <MantineAnchor href="mailto:contact@kurb.online">contact@kurb.online</MantineAnchor> or
          tell us on{" "}
          <MantineAnchor
            href="https://discord.gg/jSh7n6ju8H"
            target="_blank"
            rel="noopener noreferrer"
          >
            Discord.
          </MantineAnchor>
        </MantineText>
      </MantineBox>

      <MantineBox className={styles.filterControls}>
        <TextInput
          placeholder="Search vendors"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.currentTarget.value)}
          classNames={{
            root: styles.searchInput,
            input: styles.mantineInput,
          }}
        />

        <Select
          placeholder="Order by"
          value={orderBy}
          onChange={setOrderBy}
          data={[
            { value: "country", label: "Country" },
            { value: "alphabet", label: "Alphabetical" },
            { value: "type", label: "Store type" },
          ]}
          classNames={{
            root: styles.selectInput,
            input: styles.mantineInput,
            option: styles.selectOption,
          }}
          clearable
        />

        <Select
          placeholder="Select country"
          value={selectedCountry}
          onChange={setSelectedCountry}
          data={countries}
          classNames={{
            root: styles.selectInput,
            input: styles.mantineInput,
            option: styles.selectOption,
          }}
          searchable
          clearable
        />
      </MantineBox>

      <MantineBox className={styles.vendorGrid}>
        <MantineBox>
          {sortedGroups.map((group) => (
            <MantineBox key={group} className={styles.regionSection}>
              <MantineTitle order={2} className={styles.regionHeader}>
                {group}
              </MantineTitle>
              <MantineBox className={styles.vendorList}>
                {groupedVendors[group]?.map((vendor: Vendor) => (
                  <MantineBox key={vendor.label} className={styles.vendorCard}>
                    <UnstyledButton
                      className={styles.cardLink}
                      onClick={() => onClick(vendor)}
                      data-active={activeVendor === vendor.label}
                    >
                      <MantineText span className={styles.chevronIcon}>
                        <ChevronRightIcon />
                      </MantineText>
                      {vendor.label}
                    </UnstyledButton>
                  </MantineBox>
                ))}
              </MantineBox>
            </MantineBox>
          ))}
        </MantineBox>
      </MantineBox>

      <EntitySlideIn
        active={!!activeVendor}
        data={mapVendorToEntity(activeVendorData)}
        handleClose={closeSlideIn}
        type="vendor"
        searchParamKey="vendor"
      />
    </>
  );
};

export default VendorsList;
